import { isPblLawyer, isPblUser } from '@/utils/pblRole';

const publicPages = [
  '/',
  '/auth/forgotpassword',
  '/auth/signin',
  '/auth/signup',
  '/auth/register',
  '/auth/becomeMember',
  '/services',
  '/Subscription',
  '/About',
  '/Features',
  '/eDirectory',
  '/professionalService',
  '/pbl/lawyer/application-status',
  '/pbl/passwordless/callback',
  '/pbl/password/setup',
];

export const isApplicationSelectPath = (pathname: string) => pathname === '/select-application';

export const shouldRenderHeaderNavigation = (pathname: string) => !isApplicationSelectPath(pathname);

export const isVolunteerRegistrationPath = (pathname: string) =>
  /^\/(en|tc|sc)\/volunteerRegistration\/?$/.test(pathname);

export const isFreeLegalLinkPath = (pathname: string) => /^\/(en|tc|sc)\/freelegallink\/?$/.test(pathname);

export const isExternalRosterPath = (pathname: string) => /^\/signature-v2\/roster-invitations\/[^/]+\/?$/.test(pathname);

export const isPblPasswordlessLoginPath = (pathname: string) =>
  /^\/api\/pbl\/passwordless\/login\/[^/]+\/?$/.test(pathname);

export const isPblPasswordSetupPath = (pathname: string) => pathname.startsWith('/pbl/password/setup');

export const isPblLawyerApplicationStatusPath = (pathname: string) =>
  pathname.startsWith('/pbl/lawyer/application-status');

const normalizeNamePart = (value?: string | null) => (typeof value === 'string' ? value.trim() : '');

const joinDisplayName = (...nameParts: Array<string | null | undefined>) =>
  nameParts.map(normalizeNamePart).filter(Boolean).join(' / ');

const getAccountDisplayName = (userData?: any) => {
  const firstName = normalizeNamePart(userData?.acc_firstName);
  const lastName = normalizeNamePart(userData?.acc_lastName);

  return [firstName, lastName].filter(Boolean).join(' ');
};

const getOrderedPblUserDisplayName = (userData?: any) => {
  const firstName = normalizeNamePart(userData?.first_name ?? userData?.firstName);
  const lastName = normalizeNamePart(userData?.last_name ?? userData?.lastName);
  const firstLastName = [firstName, lastName].filter(Boolean).join(' ');

  return firstLastName || normalizeNamePart(userData?.name) || normalizeNamePart(userData?.email);
};

export const getAuthenticatedUserDisplayName = (userData?: any) => {
  const accountName = getAccountDisplayName(userData);
  const orderedPblUserName = getOrderedPblUserDisplayName(userData);

  if (isPblLawyer(userData)) {
    return (
      joinDisplayName(userData?.pbl_lawyer_profile?.full_name_en, userData?.pbl_lawyer_profile?.full_name_zh) ||
      orderedPblUserName ||
      accountName ||
      'PBL Lawyer'
    );
  }

  if (isPblUser(userData)) {
    return orderedPblUserName || accountName;
  }

  return accountName || orderedPblUserName;
};

export const PUBLIC_HEADER_MOBILE_MAX_WIDTH = 1200;
export const PBL_AUTHENTICATED_HEADER_MOBILE_MAX_WIDTH = 768;

export const shouldUseMobileHeader = ({
  isLogin,
  isTablet,
  viewportWidth,
  pathname = '',
}: {
  isLogin: boolean;
  isTablet: boolean;
  viewportWidth: number;
  pathname?: string;
}) => {
  if (!isLogin) return viewportWidth > 0 && viewportWidth <= PUBLIC_HEADER_MOBILE_MAX_WIDTH;
  if (pathname.startsWith('/pbl')) {
    return viewportWidth > 0 && viewportWidth <= PBL_AUTHENTICATED_HEADER_MOBILE_MAX_WIDTH;
  }

  return isTablet;
};

export const shouldRenderDesktopSidebar = ({
  isLogin,
  layoutController,
  useMobileHeader,
}: {
  pathname: string;
  isLogin: boolean;
  layoutController: boolean;
  useMobileHeader: boolean;
}) => !useMobileHeader && isLogin && layoutController;

export const shouldUseAuthenticatedLayout = ({
  pathname,
  isLogin,
  userData,
}: {
  pathname: string;
  isLogin: boolean;
  userData?: any;
}) => {
  if (!isLogin) return false;
  if (isApplicationSelectPath(pathname)) return false;
  if (pathname === '/eDirectory' || pathname === '/professionalService') return true;
  if (isPblLawyerApplicationStatusPath(pathname) && isPblLawyer(userData)) return true;
  if (publicPages.includes(pathname)) return false;
  if (
    isPblPasswordlessLoginPath(pathname) ||
    isPblPasswordSetupPath(pathname) ||
    isFreeLegalLinkPath(pathname) ||
    isExternalRosterPath(pathname) ||
    isVolunteerRegistrationPath(pathname)
  ) {
    return false;
  }

  return true;
};
